'use client';

/**
 * Header language switcher — ported from ThemeMaker's
 * {@code lms-portal/src/components/shell/LanguageSwitcher.tsx}.
 *
 * <p>Rendered when the tenant has more than one available locale.
 * Four visual styles, picked per-tenant via {@code i18n.switchStyle}:
 *
 * <ul>
 *   <li>{@code 'icon-popover'} — Globe icon → popover anchored below.
 *       Default. Compact, scales to many locales.</li>
 *   <li>{@code 'code-chip'} — Compact "EN ▾" pill in the header,
 *       click → popover. Reads as "I'm in English; tap to switch."</li>
 *   <li>{@code 'icon-modal'} — Globe icon → centered modal portal'd
 *       to body. Feels deliberate; good for large language lists.</li>
 *   <li>{@code 'inline-segmented'} — Horizontal segmented buttons of
 *       every code (EN | TR). Best for exactly 2 locales. Auto-falls-
 *       back to {@code 'icon-popover'} if more than 3 locales.</li>
 * </ul>
 *
 * <p>All styles share:
 * <ul>
 *   <li>{@code useTransition}-driven server action (avoids the unmount
 *       race that {@code <form action>} exhibited).</li>
 *   <li>Native-script labels (Türkçe, 한국어) over 2-letter codes.</li>
 *   <li>Active-locale highlight in brand colour.</li>
 *   <li>Pending state per-row + global {@code isPending} lockout.</li>
 *   <li>Esc / outside-click closes.</li>
 *   <li>SSR-safe (modal uses {@code createPortal} with mounted gate).</li>
 *   <li>{@code inverse} prop for trigger styling on dark headers.</li>
 * </ul>
 */
import { useEffect, useId, useRef, useState, useTransition } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';
import type { I18nConfig, LanguageSwitcherStyle } from '../../portal';
import { NATIVE_NAMES_FROM_REGISTRY as NATIVE_NAMES } from '../../portal';

export interface LanguageSwitcherProps {
  i18n: I18nConfig;
  activeLocale: string;
  setLocaleAction: (form: FormData) => void | Promise<void>;
  /** Light-on-dark trigger styling for dark chrome (cinematic / sidebar-dark). */
  inverse?: boolean;
}

// -----------------------------------------------------------------------------
// Top-level dispatcher
// -----------------------------------------------------------------------------

export function LanguageSwitcher({
  i18n,
  activeLocale,
  setLocaleAction,
  inverse = false,
}: LanguageSwitcherProps) {
  if ((i18n.available ?? []).length < 2) return null;

  // Auto-downgrade: inline-segmented overflows the header when there
  // are more than 3 locales.
  let style: LanguageSwitcherStyle = i18n.switchStyle ?? 'icon-popover';
  if (style === 'inline-segmented' && i18n.available.length > 3) {
    style = 'icon-popover';
  }

  const common = { i18n, activeLocale, setLocaleAction, inverse };

  switch (style) {
    case 'code-chip':
      return <CodeChipSwitcher {...common} />;
    case 'icon-modal':
      return <IconModalSwitcher {...common} />;
    case 'inline-segmented':
      return <InlineSegmentedSwitcher {...common} />;
    case 'icon-popover':
    default:
      return <IconPopoverSwitcher {...common} />;
  }
}

// -----------------------------------------------------------------------------
// Shared hooks
// -----------------------------------------------------------------------------

function useSelectLocale(
  activeLocale: string,
  setLocaleAction: LanguageSwitcherProps['setLocaleAction'],
) {
  const [pendingLocale, setPendingLocale] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  const select = (code: string, onDone?: () => void) => {
    if (code === activeLocale) {
      onDone?.();
      return;
    }
    setPendingLocale(code);
    startTransition(async () => {
      try {
        const fd = new FormData();
        fd.append('locale', code);
        await setLocaleAction(fd);
      } finally {
        setPendingLocale(null);
        onDone?.();
      }
    });
  };

  return { select, pendingLocale, isPending };
}

function useLabelFor(i18n: I18nConfig) {
  return (code: string) => i18n.labels?.[code] ?? NATIVE_NAMES[code] ?? code.toUpperCase();
}

// -----------------------------------------------------------------------------
// Inline SVGs (avoiding a Lucide dep in the portal bundle)
// -----------------------------------------------------------------------------

function GlobeIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function CaretIcon({ size = 12 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CheckIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// -----------------------------------------------------------------------------
// Shared list panel
// -----------------------------------------------------------------------------

interface LocaleListProps {
  i18n: I18nConfig;
  activeLocale: string;
  pendingLocale: string | null;
  isPending: boolean;
  onSelect: (code: string) => void;
  variant: 'popover' | 'modal';
}

function LocaleList({
  i18n,
  activeLocale,
  pendingLocale,
  isPending,
  onSelect,
  variant,
}: LocaleListProps) {
  const { t } = useTranslation('site-builder-runtime');
  const labelFor = useLabelFor(i18n);
  return (
    <ul
      role="listbox"
      aria-label={t('language.available')}
      style={{ listStyle: 'none', margin: 0, padding: 0 }}
    >
      {i18n.available.map((code) => {
        const active = code === activeLocale;
        const pending = pendingLocale === code;
        return (
          <li key={code}>
            <button
              type="button"
              role="option"
              aria-selected={active}
              onClick={() => onSelect(code)}
              disabled={isPending}
              lang={code}
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                gap: 12,
                padding: variant === 'modal' ? '14px 20px' : '12px 24px',
                border: 'none',
                background: 'transparent',
                color: active
                  ? 'var(--color-brand-500, #3b5bdb)'
                  : 'var(--color-text-primary, #0f1218)',
                fontWeight: active ? 600 : 400,
                fontSize: 'var(--font-size-md, 14px)',
                textAlign: variant === 'modal' ? 'left' : 'center',
                justifyContent: variant === 'modal' ? 'flex-start' : 'center',
                cursor: isPending ? 'wait' : 'pointer',
                opacity: pending ? 0.6 : 1,
                transition: 'background 120ms ease',
              }}
              onMouseEnter={(e) => {
                if (!isPending && !active) {
                  e.currentTarget.style.background =
                    'var(--color-surface-page, rgba(127, 127, 127, 0.06))';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {variant === 'modal' && active && (
                <span style={{ color: 'var(--color-brand-500, #3b5bdb)', display: 'inline-flex' }}>
                  <CheckIcon />
                </span>
              )}
              {variant === 'modal' && !active && (
                <span style={{ width: 14, display: 'inline-block' }} aria-hidden />
              )}
              <span style={{ flex: 1 }}>{labelFor(code)}</span>
              {pending && <Spinner />}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        borderRadius: '50%',
        border: '2px solid currentColor',
        borderTopColor: 'transparent',
        animation: 'ls-spin 600ms linear infinite',
        opacity: 0.6,
      }}
    />
  );
}

function GlobalKeyframes() {
  return (
    <style
      dangerouslySetInnerHTML={{
        __html: `@keyframes ls-spin { to { transform: rotate(360deg); } }`,
      }}
    />
  );
}

// -----------------------------------------------------------------------------
// Style 1 — icon-popover (default)
// -----------------------------------------------------------------------------

function IconPopoverSwitcher({
  i18n,
  activeLocale,
  setLocaleAction,
  inverse,
}: LanguageSwitcherProps) {
  const { t } = useTranslation('site-builder-runtime');
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { select, pendingLocale, isPending } = useSelectLocale(activeLocale, setLocaleAction);
  const labelFor = useLabelFor(i18n);

  useEffect(() => {
    setMounted(true);
  }, []);
  useEscapeClose(open, setOpen);

  const { popoverRef, style: popoverPositionStyle } = useSmartPopoverPosition(open, triggerRef);
  useOutsideClickCloseMulti(open, setOpen, [wrapperRef, popoverRef]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <IconTrigger
        open={open}
        inverse={!!inverse}
        isPending={isPending}
        title={labelFor(activeLocale)}
        triggerRef={triggerRef}
        onClick={() => setOpen((v) => !v)}
      />
      {open &&
        mounted &&
        popoverPositionStyle &&
        createPortal(
          <div
            ref={popoverRef}
            role="dialog"
            aria-label={t('language.change')}
            style={{ ...popoverPanelStyle(), ...popoverPositionStyle }}
          >
            <LocaleList
              i18n={i18n}
              activeLocale={activeLocale}
              pendingLocale={pendingLocale}
              isPending={isPending}
              onSelect={(c) => select(c, () => setOpen(false))}
              variant="popover"
            />
            <GlobalKeyframes />
          </div>,
          document.body,
        )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Style 2 — code-chip ("EN ▾" pill)
// -----------------------------------------------------------------------------

function CodeChipSwitcher({ i18n, activeLocale, setLocaleAction, inverse }: LanguageSwitcherProps) {
  const { t } = useTranslation('site-builder-runtime');
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { select, pendingLocale, isPending } = useSelectLocale(activeLocale, setLocaleAction);
  const labelFor = useLabelFor(i18n);

  useEffect(() => {
    setMounted(true);
  }, []);
  useEscapeClose(open, setOpen);

  const { popoverRef, style: popoverPositionStyle } = useSmartPopoverPosition(open, triggerRef);
  useOutsideClickCloseMulti(open, setOpen, [wrapperRef, popoverRef]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={isPending}
        title={labelFor(activeLocale)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          height: 36,
          padding: '0 12px',
          color: inverse ? 'rgba(255,255,255,0.95)' : 'var(--color-text-primary, #0f1218)',
          background: inverse
            ? 'rgba(255,255,255,0.10)'
            : 'var(--color-surface-muted, rgba(127,127,127,0.08))',
          border:
            '1px solid ' +
            (inverse
              ? 'rgba(255,255,255,0.15)'
              : 'var(--color-border-subtle, var(--color-neutral-200, #e5e7eb))'),
          borderRadius: 999,
          fontSize: 'var(--font-size-sm, 13px)',
          fontWeight: 600,
          cursor: 'pointer',
          opacity: isPending ? 0.6 : 1,
          transition: 'background 120ms ease',
          letterSpacing: 'var(--font-tracking-wide, 0.02em)',
        }}
      >
        <span>{activeLocale.toUpperCase()}</span>
        <span
          style={{
            display: 'inline-flex',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 150ms ease',
            opacity: 0.7,
          }}
        >
          <CaretIcon size={11} />
        </span>
      </button>
      {open &&
        mounted &&
        popoverPositionStyle &&
        createPortal(
          <div
            ref={popoverRef}
            role="dialog"
            aria-label={t('language.change')}
            style={{ ...popoverPanelStyle(), ...popoverPositionStyle }}
          >
            <LocaleList
              i18n={i18n}
              activeLocale={activeLocale}
              pendingLocale={pendingLocale}
              isPending={isPending}
              onSelect={(c) => select(c, () => setOpen(false))}
              variant="popover"
            />
            <GlobalKeyframes />
          </div>,
          document.body,
        )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Style 3 — icon-modal (centered overlay portal'd to body)
// -----------------------------------------------------------------------------

function IconModalSwitcher({
  i18n,
  activeLocale,
  setLocaleAction,
  inverse,
}: LanguageSwitcherProps) {
  const { t } = useTranslation('site-builder-runtime');
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { select, pendingLocale, isPending } = useSelectLocale(activeLocale, setLocaleAction);
  const labelFor = useLabelFor(i18n);
  const titleId = useId();

  useEffect(() => {
    setMounted(true);
  }, []);
  useEscapeClose(open, setOpen);

  return (
    <>
      <IconTrigger
        open={open}
        inverse={!!inverse}
        isPending={isPending}
        title={labelFor(activeLocale)}
        onClick={() => setOpen(true)}
      />
      {open &&
        mounted &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onClick={(e) => {
              if (e.target === e.currentTarget) setOpen(false);
            }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1100,
              background: 'rgba(0, 0, 0, 0.55)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              padding: '12vh 16px 0',
            }}
          >
            <div
              style={{
                width: 'min(440px, 100%)',
                background: 'var(--color-surface-card, #fff)',
                colorScheme: 'normal',
                color: 'var(--color-text-primary, #0f1218)',
                borderRadius: 'var(--radius-lg, 16px)',
                boxShadow: 'var(--shadow-2xl, 0 36px 72px rgba(0,0,0,0.22))',
                overflow: 'hidden',
                maxHeight: '76vh',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <header
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--space-5, 20px) var(--space-6, 24px) var(--space-3, 12px)',
                  borderBottom:
                    '1px solid var(--color-border-subtle, var(--color-neutral-200, #e5e7eb))',
                }}
              >
                <h2
                  id={titleId}
                  style={{
                    margin: 0,
                    fontSize: 'var(--font-size-lg, 16px)',
                    fontWeight: 600,
                  }}
                >
                  {t('language.select')}
                </h2>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label={t('actions.close')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--color-text-secondary, #5f6675)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-lg, 18px)',
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </header>
              <div style={{ overflowY: 'auto', padding: 'var(--space-2, 8px) 0' }}>
                <LocaleList
                  i18n={i18n}
                  activeLocale={activeLocale}
                  pendingLocale={pendingLocale}
                  isPending={isPending}
                  onSelect={(c) => select(c, () => setOpen(false))}
                  variant="modal"
                />
              </div>
              <GlobalKeyframes />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

// -----------------------------------------------------------------------------
// Style 4 — inline-segmented (EN | TR)
// -----------------------------------------------------------------------------

function InlineSegmentedSwitcher({
  i18n,
  activeLocale,
  setLocaleAction,
  inverse,
}: LanguageSwitcherProps) {
  const { t } = useTranslation('site-builder-runtime');
  const { select, pendingLocale, isPending } = useSelectLocale(activeLocale, setLocaleAction);

  return (
    <div
      role="group"
      aria-label={t('language.switcherGroup')}
      style={{
        display: 'inline-flex',
        height: 32,
        background: inverse
          ? 'rgba(255,255,255,0.08)'
          : 'var(--color-surface-muted, rgba(127,127,127,0.06))',
        border:
          '1px solid ' +
          (inverse
            ? 'rgba(255,255,255,0.12)'
            : 'var(--color-border-subtle, var(--color-neutral-200, #e5e7eb))'),
        borderRadius: 999,
        padding: 2,
      }}
    >
      {i18n.available.map((code) => {
        const active = code === activeLocale;
        const pending = pendingLocale === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => select(code)}
            disabled={isPending}
            aria-pressed={active}
            lang={code}
            style={{
              minWidth: 36,
              padding: '0 12px',
              border: 'none',
              borderRadius: 999,
              background: active
                ? inverse
                  ? 'rgba(255,255,255,0.95)'
                  : 'var(--color-surface-card, #fff)'
                : 'transparent',
              color: active
                ? inverse
                  ? 'rgba(0,0,0,0.85)'
                  : 'var(--color-text-primary, #0f1218)'
                : inverse
                  ? 'rgba(255,255,255,0.78)'
                  : 'var(--color-text-secondary, #5f6675)',
              fontWeight: active ? 700 : 500,
              fontSize: 'var(--font-size-sm, 12px)',
              letterSpacing: 'var(--font-tracking-wider, 0.04em)',
              cursor: isPending ? 'wait' : 'pointer',
              opacity: pending ? 0.6 : 1,
              boxShadow: active ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
              transition: 'background 120ms ease, color 120ms ease',
            }}
          >
            {code.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Shared trigger atoms
// -----------------------------------------------------------------------------

function IconTrigger({
  open,
  inverse,
  isPending,
  title,
  onClick,
  triggerRef,
}: {
  open: boolean;
  inverse: boolean;
  isPending: boolean;
  title: string;
  onClick: () => void;
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
}) {
  const { t } = useTranslation('site-builder-runtime');
  return (
    <button
      ref={triggerRef}
      type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={t('language.change')}
      title={title}
      disabled={isPending}
      style={{
        width: 36,
        height: 36,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        background: 'transparent',
        // Explicit colour — never rely on inherit. Inverse mode renders
        // white-on-dark; otherwise uses text-primary so the icon reads
        // at full strength against any surface.
        color: inverse ? 'rgba(255,255,255,0.95)' : 'var(--color-text-primary, #0f1218)',
        border: 'none',
        cursor: 'pointer',
        opacity: isPending ? 0.6 : 1,
        transition: 'background 120ms ease',
      }}
      onMouseEnter={(e) => {
        if (!isPending)
          e.currentTarget.style.background = inverse
            ? 'rgba(255,255,255,0.10)'
            : 'var(--color-surface-muted, rgba(127,127,127,0.10))';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <GlobeIcon />
    </button>
  );
}

// -----------------------------------------------------------------------------
// Shared utilities
// -----------------------------------------------------------------------------

function popoverPanelStyle(): CSSProperties {
  return {
    minWidth: 200,
    zIndex: 1200,
    background: 'var(--color-surface-card, #fff)',
    // Explicit color-scheme reset — opts out of inherited dark scheme
    // from cinematic-immersive headers so form controls / scrollbars
    // match the page's actual mode.
    colorScheme: 'normal',
    color: 'var(--color-text-primary, #0f1218)',
    border: '1px solid var(--color-border-subtle, var(--color-neutral-200, #e5e7eb))',
    borderRadius: 'var(--radius-lg, 12px)',
    boxShadow: 'var(--shadow-lg, 0 12px 32px rgba(0,0,0,0.14))',
    padding: 'var(--space-2, 8px) 0',
    maxHeight: '70vh',
    overflowY: 'auto',
  };
}

// -----------------------------------------------------------------------------
// Smart positioning — portal the popover to body, flip on overflow
// -----------------------------------------------------------------------------
// Solves two problems the previous absolute-positioned popover had:
//   1. Clipping by ancestor `overflow: hidden`. Portaling escapes the
//      clipping region (e.g. inside the BTK left drawer).
//   2. Off-screen placement. The previous logic always opened
//      down-right. Smart positioning measures available space after
//      mount and flips up/left as needed.

interface PopoverPosition {
  top: number;
  left: number;
  maxWidth: number;
}

function computePopoverPosition(
  triggerRect: DOMRect,
  popoverSize: { width: number; height: number },
  margin = 8,
  viewportPad = 8,
): PopoverPosition {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const spaceBelow = vh - triggerRect.bottom - viewportPad;
  const spaceAbove = triggerRect.top - viewportPad;
  const placeBelow = popoverSize.height <= spaceBelow || spaceBelow >= spaceAbove;
  const top = placeBelow
    ? triggerRect.bottom + margin
    : Math.max(viewportPad, triggerRect.top - margin - popoverSize.height);

  let left = triggerRect.right - popoverSize.width;
  if (left < viewportPad) left = triggerRect.left;
  left = Math.max(viewportPad, Math.min(left, vw - popoverSize.width - viewportPad));

  const maxWidth = vw - viewportPad * 2;
  return { top, left, maxWidth };
}

function useSmartPopoverPosition(
  open: boolean,
  triggerRef: React.RefObject<HTMLElement | null>,
): {
  popoverRef: React.RefObject<HTMLDivElement | null>;
  style: CSSProperties | null;
} {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<PopoverPosition | null>(null);

  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }

    const measure = () => {
      const trigger = triggerRef.current;
      const panel = popoverRef.current;
      if (!trigger || !panel) return;
      const tr = trigger.getBoundingClientRect();
      const pr = panel.getBoundingClientRect();
      const size = { width: pr.width || 220, height: pr.height || 280 };
      setPos(computePopoverPosition(tr, size));
    };

    measure();
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);

    let ro: ResizeObserver | null = null;
    if (popoverRef.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(popoverRef.current);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      ro?.disconnect();
    };
  }, [open, triggerRef]);

  if (!open || !pos) {
    return {
      popoverRef,
      style: open
        ? {
            position: 'fixed',
            top: -9999,
            left: -9999,
            visibility: 'hidden',
          }
        : null,
    };
  }

  return {
    popoverRef,
    style: {
      position: 'fixed',
      top: pos.top,
      left: pos.left,
      maxWidth: pos.maxWidth,
    },
  };
}

function useOutsideClickCloseMulti(
  open: boolean,
  setOpen: (v: boolean) => void,
  refs: React.RefObject<HTMLElement | null>[],
) {
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      for (const r of refs) {
        if (r.current && r.current.contains(t)) return;
      }
      setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open, setOpen]);
}

function useEscapeClose(open: boolean, setOpen: (v: boolean) => void) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, setOpen]);
}
